/**
 * O avatar de uma pessoa: as iniciais e a cor do anel.
 *
 * Duas telas mostram a mesma pessoa numa lista — `/admin/team` e o card de
 * membros de `/projects/:id/settings` — e cada uma tinha a sua cópia destas três
 * coisas, **caractere por caractere**: a mesma constante de hash, a mesma lista
 * de cores, o mesmo corte de duas palavras.
 *
 * O sintoma de divergirem não é uma tela quebrada, é pior de diagnosticar: a
 * mesma pessoa apareceria com **cor diferente** em cada tela. Ninguém abre um
 * chamado para isso e ninguém consegue explicar, porque a cor não vem de dado
 * nenhum — vem de um hash que passou a ser dois (achado C2 do review).
 */

/**
 * Até duas iniciais, em maiúsculas. `"?"` quando não dá para extrair nenhuma.
 *
 * Duas palavras, não todas: "Maria da Silva Santos" vira `MD`, não `MDSS`. É o
 * que cabe no círculo, e o `?` existe porque nome vazio ou só com espaços é dado
 * real — conta criada pelo diretório às vezes chega assim.
 */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}

/**
 * A paleta do anel. A primeira é o gradiente da marca; as outras são chapadas.
 *
 * Exportada para quem precisar afirmar sobre ela em teste — mudar a ordem muda a
 * cor de todo mundo, já que o índice sai do hash.
 */
export const AVATAR_COLORS = [
  "linear-gradient(135deg,#ffc83d,#ffb300)",
  "#08254c",
  "#1d4ea0",
  "#27a155",
  "#596b85",
  "#d4353c",
];

/**
 * Cor estável por id. Hash determinístico barato — o mesmo id dá a mesma cor em
 * qualquer tela, em qualquer sessão, sem guardar nada.
 *
 * Deliberadamente **não** é hash do nome: duas pessoas homônimas ficariam da
 * mesma cor, e alguém que troca o nome mudaria de cor sem trocar de pessoa.
 */
export function avatarColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}
